import { ComponentType, ReactNode } from "react";

export interface TextLinkProps {
  href?: string;
  children: ReactNode;
  className?: string;
  external?: boolean;
  title?: string;
  variant?: "default" | "brand" | "muted" | "unstyled";
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  asButton?: boolean;
  onPress?: () => void;
  showArrow?: boolean;
  alwaysUnderlined?: boolean;
  /**
   * Custom Link component (e.g., Next.js Link) to handle navigation
   * If provided, this component will be used instead of a standard <a> tag
   * @example LinkComponent={Link} (where Link is from 'next/link')
   */
  // biome-ignore lint/suspicious/noExplicitAny: LinkComponent needs to accept various Link implementations (Next.js, React Router, etc.) with different prop signatures
  LinkComponent?: ComponentType<any>;
  /** Data attributes for PII masking support */
  "data-pii-type"?: string;
  "data-pii-entity"?: string;
}

/**
 * TextLink
 *
 * Styled text link component for navigation and actions.
 * Supports internal/external links, button mode, and multiple visual variants.
 *
 * @param LinkComponent - Optional custom Link component (e.g., Next.js Link) for client-side navigation
 */
export const TextLink = ({
  href,
  children,
  className = "",
  external = false,
  title,
  variant = "default",
  onClick,
  asButton = false,
  onPress,
  showArrow = false,
  alwaysUnderlined = false,
  LinkComponent,
  "data-pii-type": dataPiiType,
  "data-pii-entity": dataPiiEntity,
}: TextLinkProps) => {
  const dataAttrs = {
    ...(dataPiiType && { "data-pii-type": dataPiiType }),
    ...(dataPiiEntity && { "data-pii-entity": dataPiiEntity }),
  };
  const externalProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};

  const underlineClasses = alwaysUnderlined
    ? "underline decoration-1 underline-offset-4 decoration-current"
    : "hover:underline hover:decoration-1 hover:underline-offset-4 hover:decoration-current";

  const baseClasses = `${underlineClasses} cursor-pointer`;

  const variantClasses = {
    default: "text-action-primary hover:text-action-primary-hover visited:text-action-primary",
    brand: "text-action-brand hover:text-action-brand-hover visited:text-action-brand",
    muted: "text-text-muted hover:text-text-muted visited:text-text-muted",
    unstyled: "", // No color styling, inherits from children
  };

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${className}`;

  const content = (
    <>
      {children}
      {showArrow && <span>{"\u00A0"}→</span>}
    </>
  );

  if (asButton) {
    return (
      <button type="button" className={combinedClasses} onClick={onPress} title={title} {...dataAttrs}>
        {content}
      </button>
    );
  }

  // If a custom LinkComponent is provided (e.g., Next.js Link)
  // Next.js 13+ Link component automatically renders as an <a> tag, so we just pass our styling
  if (LinkComponent && href) {
    return (
      <LinkComponent
        href={href}
        className={combinedClasses}
        title={title}
        onClick={onClick}
        {...externalProps}
        {...dataAttrs}
      >
        {content}
      </LinkComponent>
    );
  }

  // Default: render a standard <a> tag
  return (
    <a href={href || "#"} className={combinedClasses} {...externalProps} {...dataAttrs} title={title} onClick={onClick}>
      {content}
    </a>
  );
};

export default TextLink;
